/// <amd-module name="Search/Package/Components/FilterPane" />

import * as React from "react";
import * as Constants from "Search/Package/Constants";
import * as Container from "Search/Shared/Base/Container";
import * as Resources from "Search/Package/Resources";
import * as SearchPickList from "SearchUI/SearchPickList";
import * as SharedResources from "Search/Shared/Resources";
import * as SearchSharedContracts from "Search/WebApi/Contracts/Search.Shared";
import { ActionCreator } from "Search/Package/Flux/ActionCreator";
import { format } from "VSS/Core/Util/String";
import { compare } from "Search/Shared/Utils";
import { FilterBar } from "SearchUI/FilterBar"
import { IFilterStoreState, FilterType } from "Search/Shared/Base/Stores/FilterStore";
import { StoresHub, IAggregatedState, StoreName } from "Search/Package/Flux/StoresHub"
import { ViewSelectorFilterBarItem } from "Search/Package/Components/ViewSelectorFilterBarItem";
import { ISearchStoreState } from "Search/Shared/Base/Stores/SearchStore2";
import { ResultsInfo } from "Search/Shared/Components/ResultsInfo";

const searchPickListMenuMaxWidth: number = 510;

export interface IFilterPaneProps extends Container.IContainerProps<StoreName, ActionCreator, IAggregatedState, StoresHub> {
    filterStoreState: IFilterStoreState;
    searchStoreState: ISearchStoreState<SearchSharedContracts.PackageSearchRequest, SearchSharedContracts.PackageSearchResponseContent>;
    isProjectContext: boolean;
}

export const FilterPane = (props: IFilterPaneProps) => {
    const { filter, filterItems } = props.filterStoreState;
    const { onFiltersChanged } = props.actionCreator;
    const { query, response } = props.searchStoreState;

    const items = response ? response.results.length : 0;
    const totalResultsCount = response ? response.count : 0;

    return (
        <>
            <FilterBar
                filter={filter}
                isProjectContext={props.isProjectContext}>
                {
                    Object
                        .keys(filterItems)
                        .map((key: string) => {
                            const filterType = filterItems[key].filterType;
                            const visible = filterItems[key].visible;
                            const enabled = filterItems[key].enabled;

                            if (filterType === FilterType.PickList && visible) {
                                let items: SearchPickList.ISearchPickListItem[] = [];
                                if (enabled) {
                                    const facets = response && response.facets[key];
                                    if (facets) {
                                        facets.forEach((facet: SearchSharedContracts.Filter) => {
                                            items.push({
                                                id: facet.id,
                                                name: facet.name,
                                                resultCount: facet.resultCount,
                                                selected: query.filters && query.filters[key] && query.filters[key].indexOf(facet.name) > -1
                                            });
                                        });
                                    }
                                }

                                return <SearchPickList.SearchPickListFilterBarItem
                                    key={key}
                                    filterItemKey={key}
                                    menuMaxWidth={searchPickListMenuMaxWidth}
                                    isExpandable={true}
                                    {...searchPickListPropCreators[key](cloneItems(items), filterItems[key].enabled, onFiltersChanged)} />;
                            } else if (filterType === FilterType.Branch && visible) {
                                let items: SearchSharedContracts.Filter[] = [];
                                let defaultSelectedViewItem: SearchSharedContracts.Filter | undefined = undefined;

                                if (enabled) {
                                    const facets = response && response.facets[key];
                                    if (facets) {
                                        facets.forEach((facet: SearchSharedContracts.Filter) => {
                                            const item: SearchSharedContracts.Filter = {
                                                id: facet.id,
                                                name: `@${facet.name}`,
                                                resultCount: facet.resultCount
                                            };
                                            items.push(item);
                                            if (query.filters && query.filters[key] && query.filters[key].indexOf(facet.name.toLocaleLowerCase()) > -1) {
                                                defaultSelectedViewItem = item;
                                            }
                                        });
                                    }
                                }

                                return <ViewSelectorFilterBarItem
                                    key={key}
                                    filterItemKey={key}
                                    defaultSelectedViewItem={defaultSelectedViewItem}
                                    items={cloneItemsForView(items)}
                                    enabled={filterItems[key].enabled}
                                    onSelectionChanged={onFiltersChanged}
                                    onGetFooterMessage={(itemCount: number, searchText: string) => getFooterMessage(
                                        itemCount,
                                        searchText,
                                        Resources.ShowingSingleView,
                                        Resources.ShowingMultipleViews,
                                        Resources.FoundSingleView,
                                        Resources.FoundMultipleViews)} />;
                            }
                        })
                }
            </FilterBar>
            <ResultsInfo
                infoMessage={getResultsInfoMessage(items, totalResultsCount)}
                showFeedbackLink={false}
            />
        </>);
}

function cloneItems(items: SearchPickList.ISearchPickListItem[]): SearchPickList.ISearchPickListItem[] {
    const clonedItems: SearchPickList.ISearchPickListItem[] = [];
    items.forEach(item => clonedItems.push(Object.assign({}, item)));

    return clonedItems;
}

function cloneItemsForView(items: SearchSharedContracts.Filter[]): SearchSharedContracts.Filter[] {
    const clonedItems: SearchSharedContracts.Filter[] = [];
    items.forEach(item => clonedItems.push(Object.assign({}, item)));

    return clonedItems;
}

function getFooterMessage(
    filterCount: number,
    text: string,
    showingSingleItemText: string,
    showingMultipleItemsText: string,
    foundSingleItemText: string,
    foundMultipleItemsText: string): string {
    const isSearchTextPresent = !!text && text !== "";
    filterCount = !isSearchTextPresent ? filterCount - 1 : filterCount; // Adjust for "All" item.

    return !isSearchTextPresent
        ? (filterCount > 1 ? format(showingMultipleItemsText, filterCount.toString()) : showingSingleItemText)
        : (filterCount > 1
            ? format(foundMultipleItemsText, filterCount.toString())
            : filterCount === 0
                ? format(SharedResources.NoResultsForSearchText, text)
                : foundSingleItemText);
}

function filterItems(items: SearchPickList.ISearchPickListItem[], searchText: string): SearchPickList.ISearchPickListItem[] {
    searchText = searchText.toLowerCase();
    return items
        .filter((item) => item.name.toLowerCase().indexOf(searchText) >= 0)
        .sort((a, b) => compare(a.name, b.name, searchText));
}

const searchPickListPropCreators = {
    [Constants.FilterKeys.FeedFiltersKey]:
        (items: SearchPickList.ISearchPickListItem[], enabled: boolean, onSelectionChanged: (name: string) => void): SearchPickList.ISearchPickListProps => ({
            name: Constants.FilterKeys.FeedFiltersKey,
            items: items,
            allItemDisplayName: Resources.AllFeedsDisplayLabel,
            allItemLabel: SharedResources.All,
            width: 160,
            enabled: enabled,
            searchTextPlaceholder: Resources.FindFeedPlaceholder,
            displayName: Resources.FeedsDisplayLabel,
            onGetFooterMessage: (itemCount: number, searchText: string) =>
                getFooterMessage(
                    itemCount,
                    searchText,
                    Resources.ShowingSingleFeed,
                    Resources.ShowingMultipleFeeds,
                    Resources.FoundSingleFeed,
                    Resources.FoundMultipleFeeds),
            onRefineItems: filterItems,
            onSelectionChanged,
            calloutProps: {
                title: SharedResources.RefineSearchTermText,
                content: Resources.FeedFilterCalloutContent
            }
        }),
    [Constants.FilterKeys.TypeFiltersKey]:
        (items: SearchPickList.ISearchPickListItem[], enabled: boolean, onSelectionChanged: (name: string) => void): SearchPickList.ISearchPickListProps => ({
            name: Constants.FilterKeys.TypeFiltersKey,
            items: items,
            allItemDisplayName: Resources.AllPackageTypesDisplayLabel,
            allItemLabel: SharedResources.All,
            width: 160,
            enabled: enabled,
            searchTextPlaceholder: Resources.FindPackageTypePlaceholder,
            displayName: Resources.PackageTypeDisplayLabel,
            onGetFooterMessage: (itemCount: number, searchText: string) =>
                getFooterMessage(
                    itemCount,
                    searchText,
                    Resources.ShowingSingleType,
                    Resources.ShowingMultipleTypes,
                    Resources.FoundSingleType,
                    Resources.FoundMultipleTypes),
            onRefineItems: filterItems,
            onSelectionChanged,
            calloutProps: {
                title: SharedResources.RefineSearchTermText,
                content: Resources.PackageTypeFilterCalloutContent
            }
        })
};

function getResultsInfoMessage(resultsCount: number, totalCount: number): string {
    return resultsCount < totalCount
        ? format(Resources.ShowingPackageResultsFormat, resultsCount, totalCount)
        : (resultsCount === 1
            ? Resources.ShowingSinglePackageResult
            : format(Resources.ShowingNPackageResultsFormat, totalCount));
}